import restClient from 'restClient'
import { StudioSystemType } from 'types/studio/items'
import { metersToFeet } from 'util/misc'
import { getDlubalCache, getDlubalWindElevation } from '../../../dlubal'
import { getSnapnrackRafterSpacingDefault } from './SnapnrackPZSEInputForm'
import type { PZSERequestBody, RoofConstructionType, SnowExposureApi, WindExposure, YesNo } from './PZSESchema'
import { getPZSERoofAttachmentName } from './UltraRailAttachments'
import { getProfileFromRailTypeCode } from './UltraRailInputOptions'
import { trackPZSEMaxSpanFetch } from './snapnrackAmplitude'

export type { PZSERequestBody } from './PZSESchema'

export type PZSEPVRackingResult = {
  attachment_spacing_results?: Record<string, number | string>
  mount_reactions?: Record<string, unknown>
  effective_module_presure?: Record<string, unknown>
}

export type PZSEOrientation = 'portrait' | 'landscape'
export type PZSEPosition = 'interior' | 'exposed'

const ORIENTATION_PREFIX = { portrait: 'pt_', landscape: 'ls_' } as const
const POSITION_PREFIX = { interior: 'int_', exposed: 'exposed_' } as const

const DEFAULT_ZONE_SUFFIXES = ['1', '1_prime', '2_eave', '2_ridge', '2_n_edge', '3_eave', '3_ridge']

/** OpenSolar/PZSE convention default when dialog value is unset. */
export const PZSE_LEAST_HORIZONTAL_BUILDING_DIMENSION_FT = 22

const MM_TO_INCHES = 1 / 25.4
const round2 = (n: number) => Math.round(n * 100) / 100

const PZSE_SYSTEM_DERIVED_DEPENDENCY_FIELD_NAMES = new Set<
  'moduleLengthInches' | 'moduleWidthInches' | 'moduleThicknessInches' | 'roofPitch'
>(['moduleLengthInches', 'moduleWidthInches', 'moduleThicknessInches', 'roofPitch'])

function isPZSESystemDerivedDependencyField(
  fieldName: PZSEMaxSpanDependencyFieldName
): fieldName is 'moduleLengthInches' | 'moduleWidthInches' | 'moduleThicknessInches' | 'roofPitch' {
  return PZSE_SYSTEM_DERIVED_DEPENDENCY_FIELD_NAMES.has(
    fieldName as 'moduleLengthInches' | 'moduleWidthInches' | 'moduleThicknessInches' | 'roofPitch'
  )
}

let pzseMaxSpanFetchGenerationId = 0
let pzseMaxSpanFetchTargetSystemUuid: string | undefined

/** Begin a PZSE max-span fetch; stale in-flight work is ignored after invalidate or a newer begin. */
export function beginPZSEMaxSpanFetch(systemUuid: string): number {
  pzseMaxSpanFetchGenerationId += 1
  pzseMaxSpanFetchTargetSystemUuid = systemUuid
  return pzseMaxSpanFetchGenerationId
}

/** Invalidate an in-flight fetch (e.g. dialog timeout) so late responses cannot mutate cache/UI. */
export function invalidatePZSEMaxSpanFetch(generationId: number): void {
  if (generationId === pzseMaxSpanFetchGenerationId) {
    pzseMaxSpanFetchGenerationId += 1
    pzseMaxSpanFetchTargetSystemUuid = undefined
  }
}

export function isActivePZSEMaxSpanFetch(generationId?: number, targetSystemUuid?: string): boolean {
  if (generationId != null && generationId !== pzseMaxSpanFetchGenerationId) {
    return false
  }
  if (
    targetSystemUuid != null &&
    pzseMaxSpanFetchTargetSystemUuid != null &&
    targetSystemUuid !== pzseMaxSpanFetchTargetSystemUuid
  ) {
    return false
  }
  return true
}

/** Invalidate an in-flight fetch when the user selects a different system in Studio. */
export function notifyPZSESelectedSystemChanged(systemUuid: string | undefined): void {
  if (pzseMaxSpanFetchTargetSystemUuid != null && systemUuid !== pzseMaxSpanFetchTargetSystemUuid) {
    pzseMaxSpanFetchGenerationId += 1
    pzseMaxSpanFetchTargetSystemUuid = undefined
  }
}

function resolvePZSEModuleAndSlope(system: StudioSystemType): {
  moduleLengthInches: number
  moduleWidthInches: number
  moduleThicknessInches: number
  moduleName: string
  roofPitch: number
} {
  let panelSpecs: { width: number; height: number; thickness: number; name: string } | undefined
  const moduleHasDimensions = (size: unknown) => {
    return Array.isArray(size) && size.length >= 2 && typeof size[0] === 'number' && typeof size[1] === 'number'
  }
  if (typeof system.moduleType === 'function') {
    const selectedModule = system.moduleType()
    const size = selectedModule?.size

    if (selectedModule && moduleHasDimensions(size)) {
      panelSpecs = {
        width: Math.round(size[0] * 1000 * 100) / 100,
        height: Math.round(size[1] * 1000 * 100) / 100,
        thickness: selectedModule.thickness,
        name: `${selectedModule.manufacturer} ${selectedModule.code || selectedModule.id}`,
      }
    }
  }

  const moduleGrids = typeof system.moduleGrids === 'function' ? system.moduleGrids() : []
  const roofPitch = moduleGrids.length > 0 ? round2(moduleGrids[0].getSlope()) : 0

  return {
    moduleLengthInches: round2(panelSpecs ? panelSpecs.height * MM_TO_INCHES : 65.04),
    moduleWidthInches: round2(panelSpecs ? panelSpecs.width * MM_TO_INCHES : 39.13),
    moduleThicknessInches: round2(panelSpecs ? panelSpecs.thickness * MM_TO_INCHES : 1.77),
    moduleName: panelSpecs?.name ?? 'Unknown Module',
    roofPitch,
  }
}

/**
 * Get the maximum allowed attachment span (inches) from PZSE attachment_spacing_results
 * for the given orientation, position, and zones. Returns the minimum of applicable
 * max_span values (most conservative). Use for span validation and BOM.
 */
export function getMaxSpanFromPZSEResults(
  attachmentSpacingResults: Record<string, number | string> | undefined,
  orientation: PZSEOrientation,
  position: PZSEPosition,
  zoneSuffixes: string[] = DEFAULT_ZONE_SUFFIXES
): number | null {
  if (!attachmentSpacingResults || typeof attachmentSpacingResults !== 'object') return null

  const o = ORIENTATION_PREFIX[orientation]
  const p = POSITION_PREFIX[position]
  const prefix = `${o}${p}max_span_`
  let minSpan: number | null = null

  for (const zone of zoneSuffixes) {
    const key = `${prefix}${zone}`
    let val = attachmentSpacingResults[key]
    if (val === undefined && zone === '1' && position === 'exposed') {
      val = attachmentSpacingResults[`${o}${p}max_span 1`]
    }
    if (typeof val === 'number' && val > 0) {
      minSpan = minSpan === null ? val : Math.min(minSpan, val)
    }
  }

  return minSpan
}

export function buildPZSEBody(system: StudioSystemType): PZSERequestBody {
  const integration = system.integration_json?.snapnrackUltraRail as Record<string, unknown> | undefined

  // Dlubal fields — use integration values when set (matches mounting input display / manual edits).
  const windCache = getDlubalCache('wind')
  const snowCache = getDlubalCache('snow')
  const windSpeed = resolvePZSEWindSpeed(integration, windCache)
  const designSnowLoad = resolvePZSEDesignSnowLoad(integration, snowCache)
  const siteElevation = resolvePZSESiteElevationFeet()

  // PZSE fields
  const roofStructureType = (integration?.roofStructureType ?? 'gable') as string
  const roofTypeForPZSE: RoofConstructionType = roofStructureType === 'hip' ? 'Hip' : 'Gable'

  const asce7Version = (integration?.asce7Version ?? 'ASCE 7-16') as string

  const thermalFactor = (integration?.thermalFactor ?? 1.1) as number
  const roomToSlide = (integration?.roomToSlide ?? 'Yes') as YesNo
  const slipperySurface = (integration?.slipperySurface ?? 'No') as YesNo
  const reducibleSnowLoad = (integration?.reducibleSnowLoad ?? 'Yes') as YesNo
  const leastHorizontalBuildingDimension =
    typeof integration?.leastHorizontalBuildingDimension === 'number'
      ? integration.leastHorizontalBuildingDimension
      : PZSE_LEAST_HORIZONTAL_BUILDING_DIMENSION_FT
  const snowExposure = (integration?.snowExposure ?? 'Partially Exposed') as SnowExposureApi
  const windExposure = (integration?.windExposure ?? 'B') as WindExposure
  const meanRoofHeight = (integration?.meanRoofHeight ?? 0) as number
  const mountingType = (integration?.mountingType ?? 'Rafter') as 'Rafter' | 'Decking' | 'Metal'
  const rafterSpacing = (integration?.rafterSpacing ?? getSnapnrackRafterSpacingDefault(mountingType)) as number

  const clampSpacing = 0.75 as number

  const railTypeCode = integration?.railType as string | undefined
  const railProfile = getProfileFromRailTypeCode(railTypeCode) ?? 'UR-45'
  const railName = `SnapNrack ${railProfile} Rail`
  const attachmentCode = integration?.attachment as string | undefined
  const roofAttachment = getPZSERoofAttachmentName(attachmentCode, mountingType)

  const { moduleLengthInches, moduleWidthInches, moduleThicknessInches, moduleName, roofPitch } =
    resolvePZSEModuleAndSlope(system)

  return {
    system_details: {
      pv_system_type: 'Flush Mount', // confirm with PZSE the list of options for Snapnrack
      module_name: moduleName,
      module_length: moduleLengthInches,
      module_width: moduleWidthInches,
      module_thickness: moduleThicknessInches,
      rail_name: railName,
      clamp_spacing: clampSpacing,
      roof_attachment: roofAttachment,
    },
    site_parameters: {
      snow: {
        ground_snow_load: designSnowLoad,
        reducible_snow_load: reducibleSnowLoad,
        thermal_factor: thermalFactor,
        snow_exposure: snowExposure,
        slippery_surface: slipperySurface,
        room_to_slide: roomToSlide,
      },
      wind: {
        basic_wind_speed: windSpeed,
        wind_exposure: windExposure,
        site_elevation: siteElevation,
      },
      structure: {
        mean_roof_height: meanRoofHeight,
        least_horizontal_building_dimensions: leastHorizontalBuildingDimension,
        roof_pitch: roofPitch,
        panel_angle: roofPitch,
        roof_type: roofTypeForPZSE,
        framing_spacing: rafterSpacing,
      },
    },
    code: {
      asce_7_version: asce7Version,
    },
  }
}

/**
 * Request PV racking result from OpenSolar backend (which calls PZSE and caches).
 * Returns result.attachment_spacing_results (max spans in inches) and optionally mount_reactions, effective_module_presure.
 */
export async function fetchPvRackingResult(
  orgId: number,
  body: PZSERequestBody
): Promise<{ success: boolean; result?: PZSEPVRackingResult; message?: string }> {
  const api = restClient(window.API_ROOT + '/api')
  const url = `orgs/${orgId}/pzse/snapnrack-maxspan/`
  const response = await api('CUSTOM_POST', 'custom', { url, data: body })
  return response.data as { success: boolean; result?: PZSEPVRackingResult; message?: string }
}

const PZSE_CACHE_KEY = 'pzse_pv-racking'
const PZSE_RESULT_KEY = 'max_span'
const DEFAULT_ATTACHMENT_SPACING_FALLBACK = 48

export const PZSE_DEFAULT_ATTACHMENT_SPACING_INCHES = DEFAULT_ATTACHMENT_SPACING_FALLBACK

export const PZSE_ATTACHMENT_SPACING_ERROR_KEY = 'PZSE_ATTACHMENT_SPACING_ERROR'

export const PZSE_MAX_SPAN_FAILURE_WARNING_KEY = 'SNAPNRACKULTRARAIL_PZSE_MAX_SPAN_WARNING'

export const PZSE_MAX_SPAN_FAILURE_WARNING_MESSAGE =
  'Max Attachment Spacing calculation failed, open the modal to learn more. Default attachment Spacing value used to calculate BOM'

/** In-dialog copy when PZSE max-span fetch fails (differs from mounting-tab PanelGenericWarningBox text). */
export const PZSE_MAX_SPAN_DIALOG_FAILURE_WARNING_MESSAGE =
  'Unable to calculate max attachment spacing. Assumed spacing is 48 inches. Please contact your supplier for confirmation'

export const PZSE_MAX_SPAN_STALE_WARNING_KEY = 'SNAPNRACKULTRARAIL_PZSE_MAX_SPAN_STALE_WARNING'

export const PZSE_MAX_SPAN_STALE_WARNING_MESSAGE =
  'One or more inputs have changed. Please recalculate Max Attachment Spacing to get the correct value for your configuration'

/** Orange outline for PZSE dependency fields that differ from the last max-span calculation snapshot. */
export const PZSE_STALE_FIELD_OUTLINE_COLOR = '#E8811D'

const PZSE_INPUT_SNAPSHOT_KEY = 'input_snapshot'

/** Fields that affect PZSE max-span calculation (see buildPZSEBody). */
export const PZSE_MAX_SPAN_DEPENDENCY_FIELD_NAMES = [
  'windSpeed',
  'designSnowLoad',
  'roofStructureType',
  'asce7Version',
  'thermalFactor',
  'roomToSlide',
  'slipperySurface',
  'reducibleSnowLoad',
  'snowExposure',
  'windExposure',
  'meanRoofHeight',
  'leastHorizontalBuildingDimension',
  'rafterSpacing',
  'mountingType',
  'attachment',
  'railType',
  'moduleLengthInches',
  'moduleWidthInches',
  'moduleThicknessInches',
  'roofPitch',
] as const

export type PZSEMaxSpanDependencyFieldName = (typeof PZSE_MAX_SPAN_DEPENDENCY_FIELD_NAMES)[number]

type PZSECacheEntry = {
  [PZSE_RESULT_KEY]?: number | null
  success?: boolean
  [PZSE_INPUT_SNAPSHOT_KEY]?: Record<string, string | number>
}

type PZSECacheRoot = {
  systems: Record<string, PZSECacheEntry>
}

function resolvePZSESystemUuid(systemUuid?: string): string | undefined {
  return systemUuid ?? window.editor?.selectedSystem?.uuid
}

function normalizePZSECacheRoot(raw: unknown): PZSECacheRoot {
  if (!raw || typeof raw !== 'object') {
    return { systems: {} }
  }
  const obj = raw as Record<string, unknown>
  if (obj.systems && typeof obj.systems === 'object') {
    return { systems: obj.systems as Record<string, PZSECacheEntry> }
  }
  // Legacy project-wide flat cache — do not reuse across systems (see notifyPZSESelectedSystemChanged).
  return { systems: {} }
}

function readPZSECacheRoot(customData: Record<string, unknown>): PZSECacheRoot {
  return normalizePZSECacheRoot(customData[PZSE_CACHE_KEY])
}

function writePZSECacheRoot(customData: Record<string, unknown>, root: PZSECacheRoot): void {
  const hasEntries = Object.keys(root.systems).length > 0
  if (!hasEntries) {
    const { [PZSE_CACHE_KEY]: _removed, ...rest } = customData
    window.projectForm?.change('custom_data', rest)
    return
  }
  window.projectForm?.change('custom_data', {
    ...customData,
    [PZSE_CACHE_KEY]: root,
  })
}

function normalizePZSEDependencyValue(value: string | number): string {
  if (typeof value === 'number') return String(value)
  const trimmed = String(value).trim()
  if (trimmed === '') return ''
  const asNumber = Number(trimmed)
  if (!isNaN(asNumber)) return String(asNumber)
  return trimmed
}

function pzseDependencyValuesEqual(a: string | number, b: string | number): boolean {
  return normalizePZSEDependencyValue(a) === normalizePZSEDependencyValue(b)
}

function hasIntegrationDlubalValue(value: unknown): value is string | number {
  return value !== undefined && value !== null && value !== ''
}

/** Prefer integration_json (what the mounting inputs display) over Dlubal cache for wind/snow. */
function resolvePZSEWindSpeed(
  integration: Record<string, unknown> | undefined,
  windCache: ReturnType<typeof getDlubalCache>
): number {
  if (hasIntegrationDlubalValue(integration?.windSpeed)) {
    return Number(integration.windSpeed)
  }
  if (windCache?.success && windCache.value != null) {
    return windCache.value
  }
  return 0
}

function resolvePZSEDesignSnowLoad(
  integration: Record<string, unknown> | undefined,
  snowCache: ReturnType<typeof getDlubalCache>
): number {
  if (hasIntegrationDlubalValue(integration?.designSnowLoad)) {
    return Number(integration.designSnowLoad)
  }
  if (snowCache?.success && snowCache.value != null) {
    return snowCache.value
  }
  return 0
}

/** Dlubal US wind CSV elevation is SI meters; PZSE expects feet (see pzse_pv_racking_matrix.json). */
export function convertDlubalElevationMetersToFeet(elevationMeters: number): number {
  return round2(Number(metersToFeet(elevationMeters)))
}

export function resolvePZSESiteElevationFeet(): number {
  const elevationMeters = getDlubalWindElevation()
  if (elevationMeters == null) {
    return 0
  }
  return convertDlubalElevationMetersToFeet(elevationMeters)
}

function hasPZSEWindAndSnowSources(
  integration: Record<string, unknown> | undefined,
  windCache: ReturnType<typeof getDlubalCache>,
  snowCache: ReturnType<typeof getDlubalCache>
): boolean {
  const hasWind =
    hasIntegrationDlubalValue(integration?.windSpeed) ||
    (windCache?.success === true && windCache.value != null)
  const hasSnow =
    hasIntegrationDlubalValue(integration?.designSnowLoad) ||
    (snowCache?.success === true && snowCache.value != null)
  return hasWind && hasSnow
}

export function capturePZSEMaxSpanInputSnapshot(system: StudioSystemType): Record<string, string | number> {
  const integration = system.integration_json?.snapnrackUltraRail as Record<string, unknown> | undefined
  const windCache = getDlubalCache('wind')
  const snowCache = getDlubalCache('snow')
  const windSpeed = resolvePZSEWindSpeed(integration, windCache)
  const designSnowLoad = resolvePZSEDesignSnowLoad(integration, snowCache)
  const { moduleLengthInches, moduleWidthInches, moduleThicknessInches, roofPitch } =
    resolvePZSEModuleAndSlope(system)

  const snapshot: Record<string, string | number> = {}
  for (const fieldName of PZSE_MAX_SPAN_DEPENDENCY_FIELD_NAMES) {
    if (fieldName === 'windSpeed') {
      snapshot.windSpeed = windSpeed
      continue
    }
    if (fieldName === 'designSnowLoad') {
      snapshot.designSnowLoad = designSnowLoad
      continue
    }
    if (fieldName === 'moduleLengthInches') {
      snapshot.moduleLengthInches = moduleLengthInches
      continue
    }
    if (fieldName === 'moduleWidthInches') {
      snapshot.moduleWidthInches = moduleWidthInches
      continue
    }
    if (fieldName === 'moduleThicknessInches') {
      snapshot.moduleThicknessInches = moduleThicknessInches
      continue
    }
    if (fieldName === 'roofPitch') {
      snapshot.roofPitch = roofPitch
      continue
    }
    const value = integration?.[fieldName]
    if (value !== undefined && value !== null) {
      snapshot[fieldName] = value as string | number
    }
  }
  return snapshot
}

export function getPZSEMaxSpanInputSnapshot(systemUuid?: string): Record<string, string | number> | null {
  const cached = readPZSECacheEntry(systemUuid)
  const snapshot = cached?.[PZSE_INPUT_SNAPSHOT_KEY]
  if (!snapshot || typeof snapshot !== 'object') return null
  return snapshot
}

/** Dlubal fields are edited live on integration_json; do not mask them with dialog draft values. */
const PZSE_INTEGRATION_SYNCED_DEPENDENCY_FIELD_NAMES = ['windSpeed', 'designSnowLoad'] as const

export function getPZSEDraftOverridesForStaleDetection(
  overrides: Record<string, string | number>
): Record<string, string | number> {
  const result = { ...overrides }
  for (const fieldName of PZSE_INTEGRATION_SYNCED_DEPENDENCY_FIELD_NAMES) {
    delete result[fieldName]
  }
  return result
}

export function getPZSEStaleDependencyFieldNames(
  system: StudioSystemType,
  overrides?: Record<string, string | number>
): PZSEMaxSpanDependencyFieldName[] {
  const snapshot = getPZSEMaxSpanInputSnapshot(system.uuid)
  if (!snapshot) return []

  const current = capturePZSEMaxSpanInputSnapshot(system)
  if (overrides) {
    for (const fieldName of PZSE_MAX_SPAN_DEPENDENCY_FIELD_NAMES) {
      if (overrides[fieldName] !== undefined) {
        current[fieldName] = overrides[fieldName]
      }
    }
  }

  const stale: PZSEMaxSpanDependencyFieldName[] = []
  for (const fieldName of PZSE_MAX_SPAN_DEPENDENCY_FIELD_NAMES) {
    const snapshotValue = snapshot[fieldName]
    const currentValue = current[fieldName]
    if (snapshotValue === undefined && currentValue === undefined) continue
    if (
      snapshotValue === undefined ||
      currentValue === undefined ||
      !pzseDependencyValuesEqual(snapshotValue, currentValue)
    ) {
      stale.push(fieldName)
    }
  }
  return stale
}

export function shouldShowPZSEMaxSpanStaleWarning(system: StudioSystemType): boolean {
  return getPZSEStaleDependencyFieldNames(system).length > 0
}

export function clearPZSEMaxSpanStaleWarning(systemUuid: string): void {
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(PZSE_MAX_SPAN_STALE_WARNING_KEY, systemUuid, 'plugin')
}

/** Sync standard mounting-tab warning when max-span dependency inputs differ from last calculation. */
export function syncPZSEMaxSpanStaleWarning(system: StudioSystemType): void {
  const systemUuid = system.uuid
  if (shouldShowPZSEMaxSpanStaleWarning(system)) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate(PZSE_MAX_SPAN_STALE_WARNING_MESSAGE),
      key: PZSE_MAX_SPAN_STALE_WARNING_KEY,
      severity: 'warning',
      systemId: systemUuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
  } else {
    clearPZSEMaxSpanStaleWarning(systemUuid)
  }
}

export function resetPZSEMaxSpanInputsToSnapshot(system: StudioSystemType): boolean {
  const snapshot = getPZSEMaxSpanInputSnapshot(system.uuid)
  if (!snapshot) return false

  const integration = system.integration_json?.snapnrackUltraRail as Record<string, unknown> | undefined
  if (!integration) return false

  const updates: Record<string, unknown> = { ...integration }
  for (const fieldName of PZSE_MAX_SPAN_DEPENDENCY_FIELD_NAMES) {
    if (isPZSESystemDerivedDependencyField(fieldName)) {
      continue
    }
    if (snapshot[fieldName] !== undefined) {
      updates[fieldName] = snapshot[fieldName]
    }
  }

  system.integration_json = {
    ...system.integration_json,
    snapnrackUltraRail: updates,
  }

  if (window.editor?.signals) {
    window.editor.signals.objectChanged.dispatch(system, 'mounting')
  }

  syncPZSEMaxSpanStaleWarning(system)
  return true
}

function readPZSECacheEntry(systemUuid?: string): PZSECacheEntry | null {
  if (!window.projectForm) return null

  const resolvedSystemUuid = resolvePZSESystemUuid(systemUuid)
  if (!resolvedSystemUuid) return null

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}
  const root = readPZSECacheRoot(customData)
  return root.systems[resolvedSystemUuid] ?? null
}

export function shouldShowPZSEMaxSpanFailureWarning(system: StudioSystemType): boolean {
  const cached = readPZSECacheEntry(system.uuid)
  return cached?.success === false
}

export function clearPZSEMaxSpanFailureWarning(systemUuid: string): void {
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(PZSE_MAX_SPAN_FAILURE_WARNING_KEY, systemUuid, 'plugin')
}

/** Sync standard mounting-tab warning (PanelGenericWarningBox category="mounting"). */
export function syncPZSEMaxSpanFailureWarning(system: StudioSystemType): void {
  const systemUuid = system.uuid
  if (shouldShowPZSEMaxSpanFailureWarning(system)) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate(PZSE_MAX_SPAN_FAILURE_WARNING_MESSAGE),
      key: PZSE_MAX_SPAN_FAILURE_WARNING_KEY,
      severity: 'warning',
      systemId: systemUuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
  } else {
    clearPZSEMaxSpanFailureWarning(systemUuid)
  }
}

export function clearPZSEAttachmentSpacingError(systemUuid: string): void {
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(PZSE_ATTACHMENT_SPACING_ERROR_KEY, systemUuid, 'validation')
}

/**
 * Validates attachment spacing against PZSE max span.
 * Adds a design error if spacing exceeds max span; clears error otherwise.
 */
export function validatePZSEAttachmentSpacing(systemUuid: string, attachmentSpacing: number): void {
  const cached = getPZSECache(systemUuid)
  const maxSpan = cached?.value ?? DEFAULT_ATTACHMENT_SPACING_FALLBACK

  const clearError = () => clearPZSEAttachmentSpacingError(systemUuid)

  if (!cached?.success || maxSpan == null) {
    clearError()
    return
  }

  const numAttachmentSpacing = Number(attachmentSpacing)
  if (isNaN(numAttachmentSpacing)) {
    clearError()
    return
  }

  if (numAttachmentSpacing > maxSpan) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate('Please reduce attachment spacing below the max of %{max} inches', { max: maxSpan }),
      key: PZSE_ATTACHMENT_SPACING_ERROR_KEY,
      severity: 'warning',
      systemId: systemUuid,
      source: 'validation',
      category: 'mounting',
      options: {},
    })
  } else {
    clearError()
  }
}

export interface PZSECacheData {
  value: number | null
  success: boolean
}

/** Spacing shown under max-span status (cache value, else saved or draft attachment spacing). */
export function getPZSEDisplayedCalculatedSpacingInches(
  mountingJson?: Record<string, unknown>,
  overrides?: Record<string, string | number>,
  systemUuid?: string
): number {
  const pzseCache = getPZSECache(systemUuid)
  if (pzseCache?.value != null) {
    return pzseCache.value
  }

  const raw = overrides?.attachmentSpacingInches ?? mountingJson?.attachmentSpacingInches
  if (raw === undefined || raw === null || raw === '') {
    return 0
  }

  const parsed = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(parsed) ? parsed : 0
}

export function getPZSECache(systemUuid?: string): PZSECacheData | null {
  const cached = readPZSECacheEntry(systemUuid)
  if (!cached) return null

  const hasValue = cached[PZSE_RESULT_KEY] !== null && cached[PZSE_RESULT_KEY] !== undefined
  const isSuccess = cached.success === true

  if (isSuccess && !hasValue) {
    console.warn(`Invalid PZSE cache state: success=true but ${PZSE_RESULT_KEY}=${cached[PZSE_RESULT_KEY]}`)
    return null
  }

  return {
    value: hasValue ? (cached[PZSE_RESULT_KEY] as number) : null,
    success: isSuccess,
  }
}

export function setPZSECacheSuccess(value: number, systemUuid: string, system?: StudioSystemType): void {
  if (!window.projectForm) return

  if (typeof value !== 'number' || isNaN(value)) {
    console.error(`Invalid value for PZSE cache success: ${value}`)
    return
  }

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}
  const targetSystem = system ?? window.editor?.selectedSystem
  const inputSnapshot = targetSystem ? capturePZSEMaxSpanInputSnapshot(targetSystem) : undefined
  const root = readPZSECacheRoot(customData)
  root.systems[systemUuid] = {
    [PZSE_RESULT_KEY]: value,
    success: true,
    ...(inputSnapshot ? { [PZSE_INPUT_SNAPSHOT_KEY]: inputSnapshot } : {}),
  }
  writePZSECacheRoot(customData, root)

  if (targetSystem && targetSystem.uuid === systemUuid) {
    syncPZSEMaxSpanFailureWarning(targetSystem)
    syncPZSEMaxSpanStaleWarning(targetSystem)
  }
}

export function setPZSECacheFailure(systemUuid: string, system?: StudioSystemType): void {
  if (!window.projectForm) return

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}
  const targetSystem = system ?? window.editor?.selectedSystem
  const inputSnapshot = targetSystem ? capturePZSEMaxSpanInputSnapshot(targetSystem) : undefined
  const root = readPZSECacheRoot(customData)
  root.systems[systemUuid] = {
    [PZSE_RESULT_KEY]: null,
    success: false,
    ...(inputSnapshot ? { [PZSE_INPUT_SNAPSHOT_KEY]: inputSnapshot } : {}),
  }
  writePZSECacheRoot(customData, root)

  if (targetSystem && targetSystem.uuid === systemUuid) {
    syncPZSEMaxSpanFailureWarning(targetSystem)
    syncPZSEMaxSpanStaleWarning(targetSystem)
  }
}

export function clearPZSECache(systemUuid?: string): void {
  if (!window.projectForm) return

  const resolvedSystemUuid = resolvePZSESystemUuid(systemUuid)
  if (!resolvedSystemUuid) return

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}
  const root = readPZSECacheRoot(customData)
  if (!root.systems[resolvedSystemUuid]) return

  delete root.systems[resolvedSystemUuid]
  writePZSECacheRoot(customData, root)
}

function applyAttachmentSpacingToInput(system: StudioSystemType, value: number): void {
  const integration = system.integration_json?.snapnrackUltraRail as Record<string, unknown> | undefined
  if (!integration || !window.editor?.signals) return

  system.integration_json = {
    ...system.integration_json,
    snapnrackUltraRail: { ...integration, attachmentSpacingInches: value },
  }
  window.editor.signals.objectChanged.dispatch(system, 'mounting')
}

function applyMaxSpanToInput(system: StudioSystemType, value: number): void {
  applyAttachmentSpacingToInput(system, value)
}

export function applyDefaultAttachmentSpacingOnFailure(system: StudioSystemType): void {
  applyAttachmentSpacingToInput(system, DEFAULT_ATTACHMENT_SPACING_FALLBACK)
}

/**
 * Fetch the most conservative PZSE max span and cache the result.
 * Only call from the PZSE dialog button (with `force: true`). Does not retry after failure
 * unless the user explicitly triggers another fetch.
 */
export async function fetchPZSEMaxSpanData(options?: {
  force?: boolean
  generationId?: number
  systemUuid?: string
}): Promise<void> {
  const generationId = options?.generationId
  const targetSystem = window.editor?.selectedSystem
  const targetSystemUuid = options?.systemUuid ?? targetSystem?.uuid
  const isActive = () =>
    isActivePZSEMaxSpanFetch(generationId, targetSystemUuid) &&
    (targetSystemUuid == null || window.editor?.selectedSystem?.uuid === targetSystemUuid)

  if (!options?.force) {
    const cachedData = getPZSECache(targetSystemUuid)
    if (cachedData != null) return
  } else if (targetSystemUuid) {
    clearPZSECache(targetSystemUuid)
  }

  const windCache = getDlubalCache('wind')
  const snowCache = getDlubalCache('snow')
  const integration = targetSystem?.integration_json?.snapnrackUltraRail as Record<string, unknown> | undefined

  if (!hasPZSEWindAndSnowSources(integration, windCache, snowCache)) {
    if (!isActive() || !targetSystem || !targetSystemUuid) return
    const msg = window.translate(
      'Failed to calculate max attachment spacing. Please fetch Dlubal wind and snow first, then try again.'
    )
    window.Designer?.showNotification(msg, 'error')
    setPZSECacheFailure(targetSystemUuid, targetSystem)
    applyDefaultAttachmentSpacingOnFailure(targetSystem)
    trackPZSEMaxSpanFetch(false, null, { systemId: targetSystemUuid })
    return
  }

  const orgId = (window as any).WorkspaceHelper?.project?.org_id as number | undefined

  if (!targetSystem || !targetSystemUuid || !orgId) {
    if (!isActive()) return
    console.error('System or orgId not available for PZSE fetch')
    if (targetSystem && targetSystemUuid) {
      setPZSECacheFailure(targetSystemUuid, targetSystem)
      applyDefaultAttachmentSpacingOnFailure(targetSystem)
    }
    trackPZSEMaxSpanFetch(false, null, { systemId: targetSystemUuid })
    return
  }

  try {
    const body = buildPZSEBody(targetSystem)
    const response = await fetchPvRackingResult(orgId, body)

    if (!isActive()) return

    if (!options?.force && getPZSECache(targetSystemUuid)?.success === true) return

    if (!response.success || !response.result?.attachment_spacing_results) {
      const message =
        response.message ||
        window.translate('Unable to calculate max attachment spacing. Check mounting inputs and try again.')
      window.Designer?.showNotification(message, 'error')
      setPZSECacheFailure(targetSystemUuid, targetSystem)
      applyDefaultAttachmentSpacingOnFailure(targetSystem)
      trackPZSEMaxSpanFetch(false, null, { systemId: targetSystemUuid })
      return
    }

    const spans = response.result.attachment_spacing_results
    const candidates = [
      getMaxSpanFromPZSEResults(spans, 'portrait', 'interior'),
      getMaxSpanFromPZSEResults(spans, 'portrait', 'exposed'),
      getMaxSpanFromPZSEResults(spans, 'landscape', 'interior'),
      getMaxSpanFromPZSEResults(spans, 'landscape', 'exposed'),
    ].filter((v): v is number => v !== null)

    const mostConservativeMaxSpan = candidates.length > 0 ? Math.min(...candidates) : null

    if (!isActive()) return

    if (mostConservativeMaxSpan !== null) {
      setPZSECacheSuccess(mostConservativeMaxSpan, targetSystemUuid, targetSystem)
      applyMaxSpanToInput(targetSystem, mostConservativeMaxSpan)
      trackPZSEMaxSpanFetch(true, mostConservativeMaxSpan, { systemId: targetSystemUuid })
    } else {
      window.Designer?.showNotification(
        window.translate('Unable to calculate max attachment spacing. Check mounting inputs and try again.'),
        'error'
      )
      setPZSECacheFailure(targetSystemUuid, targetSystem)
      applyDefaultAttachmentSpacingOnFailure(targetSystem)
      trackPZSEMaxSpanFetch(false, null, { systemId: targetSystemUuid })
    }
  } catch (err) {
    if (!isActive()) return
    console.error('PZSE max span fetch failed\n', err)
    window.Designer?.showNotification(
      window.translate('Unable to calculate max attachment spacing. Check mounting inputs and try again.'),
      'error'
    )
    setPZSECacheFailure(targetSystemUuid, targetSystem)
    applyDefaultAttachmentSpacingOnFailure(targetSystem)
    trackPZSEMaxSpanFetch(false, null, { systemId: targetSystemUuid })
  }
}
